import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  CurrentUser,
  login as apiLogin,
  register as apiRegister,
  logout as apiLogout,
  refresh as apiRefresh,
  me as apiMe,
} from '../services/authService';
import { setAccessToken, onSessionExpired } from '../lib/api';

interface AuthContextType {
  user: CurrentUser | null;
  isAuthenticated: boolean;
  isInitializing: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;
  /**
   * Used by the OAuth callback page after the BE redirects with ?session=ok.
   * Triggers a /auth/refresh via the cookie that was just set + populates the user.
   */
  finalizeExternalLogin: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps { children: ReactNode; }

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // ─── Boot: try to restore the session via the refresh cookie ──────────────
  // Browser cookies survive page reload; if the user was logged in last time,
  // we exchange the cookie for a fresh access token + user payload silently.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const auth = await apiRefresh();
        if (cancelled) return;
        setAccessToken(auth.accessToken);
        setUser(auth.user);
      } catch {
        // No cookie / expired / revoked — user is unauthenticated. Normal path.
      } finally {
        if (!cancelled) setIsInitializing(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ─── Handle silent-refresh failures from the api interceptor ──────────────
  useEffect(() => {
    onSessionExpired(() => {
      setUser(null);
      setAccessToken(null);
    });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const auth = await apiLogin(email, password);
    setAccessToken(auth.accessToken);
    setUser(auth.user);
  }, []);

  const register = useCallback(async (email: string, password: string, displayName: string) => {
    await apiRegister(email, password, displayName);
    // BE sends a confirmation email; user logs in after confirming.
  }, []);

  const logout = useCallback(async () => {
    try { await apiLogout(); } finally {
      setAccessToken(null);
      setUser(null);
    }
  }, []);

  const finalizeExternalLogin = useCallback(async () => {
    // Cookie was set by the BE OAuth callback redirect; pull the access token now.
    const auth = await apiRefresh();
    setAccessToken(auth.accessToken);
    setUser(auth.user);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: user !== null,
      isInitializing,
      login,
      register,
      logout,
      finalizeExternalLogin,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
